import {apiFetch} from '../../../shared/apiFetch';
import {API_CONFIG, ERROR_MESSAGES} from '../../config';
import {DEFAULT_VARS, PRODUCT_CATALOG, uid} from '../../constants';

// ── Helpers ──────────────────────────────────────────────────────────

const stringifyValue = (value) => {
    if (value == null) return '';
    if (Array.isArray(value)) return value.map(stringifyValue).filter(Boolean).join(', ');
    if (typeof value === 'object') return Object.values(value).map(stringifyValue).filter(Boolean).join(', ');
    return String(value).trim();
};

const toVarName = (value) =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const pushUniqueVar = (variables, seenNames, name, sample) => {
    const cleanName = toVarName(name);
    const cleanSample = stringifyValue(sample);
    if (!cleanName || !cleanSample || seenNames.has(cleanName)) return;
    variables.push({id: `v${uid()}`, name: cleanName, sample: cleanSample});
    seenNames.add(cleanName);
};

const buildTopicTemplate = (vendor, productVars) => {
    const varNames = new Set(productVars.map((v) => v.name));
    const segments = [`${vendor || 'Shopify'} {{product_name}}`];
    if (varNames.has('product_type')) segments.push('{{product_type}}');
    else if (varNames.has('material')) segments.push('{{material}}');
    return segments.join(' — ');
};

const buildFieldTemplates = (productVars) => {
    const varNames = new Set(productVars.map((v) => v.name));
    const token = (name) => (varNames.has(name) ? `{{${name}}}` : '');
    const join = (names, fallback) => {
        const vals = names.map(token).filter(Boolean);
        return vals.length ? vals.join(', ') : fallback;
    };
    return [
        {field: 'Key benefit', kw: join(['product_type', 'tags'], 'premium quality')},
        {field: 'Material', kw: join(['material', 'finish'], 'durable materials')},
        {field: 'Design style', kw: join(['product_type', 'vendor'], 'refined product design')},
        {field: 'Price point', kw: join(['price', 'compare_at_price'], 'competitive value')},
        {field: 'Ideal for', kw: join(['product_type', 'tags'], 'home and commercial use')},
    ];
};

const createFallbackProductVars = (product) => {
    const merged = DEFAULT_VARS.map((v) => ({...v, id: `v${uid()}`}));
    const nameVar = merged.find((v) => v.name === 'product_name');
    const skuVar = merged.find((v) => v.name === 'sku');
    if (nameVar) nameVar.sample = product.name;
    if (skuVar) skuVar.sample = product.sku || product.id;
    return merged;
};

// ── ProductService (Shopify via proxy) ───────────────────────────────

class ProductService {
    constructor() {
        this.proxyUrl = API_CONFIG.PROXY_URL;
        this.timeout = API_CONFIG.TIMEOUT;
    }

    createTimeoutController(timeoutMs = this.timeout) {
        if (typeof AbortController === 'undefined') return null;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        return {
            signal: controller.signal,
            cancel: () => clearTimeout(timeoutId),
        };
    }

    getFallbackProducts() {
        return new Map(
            PRODUCT_CATALOG.map((product) => [
                product.sku || product.id,
                {
                    ...product,
                    id: product.sku || product.id,
                    sku: product.sku || product.id,
                    vendor: 'Demo',
                    collection: 'Demo',
                    vars: createFallbackProductVars(product),
                },
            ]),
        );
    }

    // Fetch collections from the Shopify proxy
    async getCollections() {
        const tc = this.createTimeoutController();
        try {
            const res = await apiFetch(`${this.proxyUrl}/collections`, {
                signal: tc?.signal,
            });
            if (!res.ok) throw new Error(`Failed to fetch collections: ${res.statusText}`);
            const data = await res.json();
            return data.collections || [];
        } finally {
            tc?.cancel();
        }
    }

    // Fetch products from the Shopify proxy, optionally filtered by collection
    async getProducts({collectionId} = {}) {
        if (typeof fetch !== 'function') return null;
        const tc = this.createTimeoutController();
        try {
            const params = new URLSearchParams({limit: '250'});
            if (collectionId) params.set('collection_id', collectionId);

            const res = await apiFetch(`${this.proxyUrl}/products?${params}`, {
                signal: tc?.signal,
            });
            if (!res.ok) throw new Error(`Failed to fetch products: ${res.statusText}`);
            const data = await res.json();
            return data.products || [];
        } finally {
            tc?.cancel();
        }
    }

    // Update a product on Shopify via the proxy
    async updateProduct(productId, updates) {
        const tc = this.createTimeoutController();
        try {
            const res = await apiFetch(`${this.proxyUrl}/products/${productId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(updates),
                signal: tc?.signal,
            });
            if (!res.ok) {
                let details = res.statusText || `HTTP ${res.status || 'error'}`;
                try {
                    if (typeof res.json !== 'function') throw new Error('Response has no json body');
                    const data = await res.json();
                    details = data?.error || details;
                } catch {
                    if (typeof res.text === 'function') {
                        details = (await res.text()) || details;
                    }
                }
                throw new Error(`Failed to update product: ${details}`);
            }
            return res.json();
        } finally {
            tc?.cancel();
        }
    }

    // Build variable list from a Shopify product (with variant + options)
    buildProductVars({product, collection, includeSetChildren = true}) {
        const variables = [];
        const seen = new Set();
        const v = product.variant || {};
        const mf = product.metafields || {};
        const options = product.options || {};

        pushUniqueVar(variables, seen, 'product_name', product.title);
        pushUniqueVar(variables, seen, 'sku', v.sku);
        pushUniqueVar(variables, seen, 'variant_title', v.title);
        pushUniqueVar(variables, seen, 'vendor', product.vendor);
        pushUniqueVar(variables, seen, 'collection', collection);
        pushUniqueVar(variables, seen, 'product_type', product.product_type);
        pushUniqueVar(variables, seen, 'price', v.price ? `$${v.price}` : '');
        pushUniqueVar(variables, seen, 'compare_at_price', v.compare_at_price ? `$${v.compare_at_price}` : '');
        pushUniqueVar(variables, seen, 'weight', v.weight ? `${v.weight} ${v.weight_unit || 'lb'}` : '');
        pushUniqueVar(variables, seen, 'tags', product.tags);
        pushUniqueVar(variables, seen, 'handle', product.handle);
        pushUniqueVar(variables, seen, 'status', product.status);
        pushUniqueVar(variables, seen, 'barcode', v.barcode);
        pushUniqueVar(variables, seen, 'description', product.body_html?.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim());

        // Add variant options as variables (e.g. Color, Size)
        Object.entries(options).forEach(([name, value]) => {
            pushUniqueVar(variables, seen, name, value);
        });

        // Add metafields as variables
        Object.entries(mf).forEach(([key, value]) => {
            pushUniqueVar(variables, seen, key.replace(/\./g, '_'), value);
        });

        if (includeSetChildren) {
            (product.set_children || []).forEach((child, childIndex) => {
                const childSku = child.variant?.sku;
                if (!childSku) return;
                const savedPosition = Number(child.set_position);
                const componentPosition = Number.isInteger(savedPosition) && savedPosition > 0
                    ? savedPosition
                    : childIndex + 1;
                const childVars = this.buildProductVars({
                    product: child,
                    includeSetChildren: false,
                });
                childVars.forEach((childVar) => {
                    pushUniqueVar(
                        variables,
                        seen,
                        `component${componentPosition}_${childVar.name}`,
                        childVar.sample,
                    );
                });
            });
        }

        return variables;
    }

    // Normalize a single Shopify product/variant into the app's expected shape
    normalizeProduct(shopifyProduct, {collection = ''} = {}) {
        const v = shopifyProduct.variant || {};
        const options = shopifyProduct.options || {};
        const sku = v.sku || `${shopifyProduct.id}-${v.id || '0'}`;
        const variantLabel = v.title ? ` - ${v.title}` : '';
        const productVars = this.buildProductVars({product: shopifyProduct, collection});

        return {
            id: sku,
            sku,
            shopifyId: shopifyProduct.id,
            variantId: v.id,
            name: shopifyProduct.title + variantLabel,
            category: shopifyProduct.product_type || 'Uncategorized',
            vendor: shopifyProduct.vendor || '',
            collection,
            options,
            imageUrl: shopifyProduct.variantImage || shopifyProduct.images?.[0] || '',
            images: shopifyProduct.images || [],
            topic: buildTopicTemplate(shopifyProduct.vendor, productVars),
            fields: buildFieldTemplates(productVars),
            vars: productVars,
            raw: shopifyProduct,
        };
    }

    // Normalize an array of Shopify products
    normalizeProducts(shopifyProducts, {collection = ''} = {}) {
        if (!shopifyProducts || !shopifyProducts.length) {
            throw new Error(ERROR_MESSAGES.PRODUCT_SYNC_EMPTY);
        }

        const entries = shopifyProducts
            .map((p) => this.normalizeProduct(p, {collection}))
            .sort((a, b) => (a.sku || a.id).localeCompare(b.sku || b.id));

        if (!entries.length) throw new Error(ERROR_MESSAGES.PRODUCT_SYNC_EMPTY);

        return {
            products: new Map(entries.map((p) => [p.id, p])),
            source: 'shopify',
        };
    }
}

// ── Exported utilities (kept for compatibility) ──────────────────────

export const buildAggregatedProductVars = (selectedProducts) => {
    const valuesByName = new Map();
    const pushValue = (name, sample) => {
        const clean = stringifyValue(sample);
        if (!name || !clean) return;
        if (!valuesByName.has(name)) valuesByName.set(name, []);
        const current = valuesByName.get(name);
        if (!current.includes(clean)) current.push(clean);
    };

    selectedProducts.forEach((product) => {
        (product.vars || []).forEach((v) => pushValue(v.name, v.sample));
    });
    pushValue('selected_skus', selectedProducts.map((p) => p.sku || p.id).join(', '));
    pushValue('selected_products', selectedProducts.map((p) => p.name).join(', '));

    return Array.from(valuesByName.entries()).map(([name, samples]) => ({
        id: `v${uid()}`,
        name,
        sample: samples.join(' | '),
    }));
};

export const mergeProductVars = (currentVars, productVars) => {
    const nextByName = new Map(productVars.map((v) => [v.name, v]));
    const consumed = new Set();

    const merged = currentVars.map((v) => {
        const replacement = nextByName.get(v.name);
        if (!replacement) return v;
        consumed.add(v.name);
        return {...v, sample: replacement.sample};
    });

    productVars.forEach((v) => {
        if (!consumed.has(v.name) && !merged.some((m) => m.name === v.name)) {
            merged.push(v);
        }
    });

    return merged;
};

export const reconcileProductVars = (currentVars, productVars, previousProductVarNames = []) => {
    const previousNames = new Set(previousProductVarNames);
    const nextByName = new Map(productVars.map((v) => [v.name, v]));
    const merged = [];

    currentVars.forEach((v) => {
        const replacement = nextByName.get(v.name);
        if (replacement) {
            merged.push({...v, sample: replacement.sample});
            nextByName.delete(v.name);
            return;
        }
        if (!previousNames.has(v.name)) merged.push(v);
    });

    nextByName.forEach((v) => merged.push(v));
    return merged;
};

export const productService = new ProductService();
